/* eslint-disable @nx/enforce-module-boundaries */
import { Injectable } from '@angular/core';

import {
    catchError,
    map,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as fromShipments from './shipments.reducer';
import * as ShipmentsSelectors from './shipments.selectors';

import { ResetCustomFields } from '@qwyk/portals/custom-fields';
import { ResetProofOfDeliveries } from '@qwyk/portals/proof-of-delivery';

import { ResetShipmentPackages } from '@qwyk/shared-stores/shipment-packages';
import { ResetShipmentAddresses } from '@qwyk/shared-stores/shipment-addresses';
import { ResetShipmentRelations } from '@qwyk/shared-stores/shipment-relations';
import { ResetShipmentActivities } from '@qwyk/shared-stores/shipment-activities';
import { ResetShipmentContainers } from '@qwyk/shared-stores/shipment-containers';
import { ResetShipmentAttachments } from '@qwyk/shared-stores/shipment-attachments';
import { ResetShipmentTrackingEvents } from '@qwyk/shared-stores/shipment-tracking-events';
import { ResetShipmentTimelineEvents } from '@qwyk/shared-stores/shipment-timeline-events';

import { ShipmentsActions } from './shipments.actions';
import { ShipmentsServiceBase } from '../shipments-service-base';
import { WorkerManagerService } from '../services/worker-manager.service';

@Injectable()
export class ShipmentsEffects {
    loadShipments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.loadShipments),
            withLatestFrom(
                this.store.select(ShipmentsSelectors.getBackendQuery)
            ),
            switchMap(([action, query]) =>
                this.service
                    .getShipments({
                        ...action.query,
                        ...query
                    })
                    .pipe(
                        take(1),
                        map(response => {
                            return ShipmentsActions.loadShipmentsSuccess({
                                shipments: response.data,
                                customFields:
                                    response?.extra
                                        ?.custom_field_definitions ?? [],
                                customViews:
                                    response?.extra?.custom_table_views ??
                                    [],
                                backendPagination: response?.meta
                            })
                        }),
                        catchError(error => {
                            console.error(error);
                            return of(
                                ShipmentsActions.loadShipmentsFailure({ error })
                            );
                        })
                    )
            )
        )
    );

    loadDraftShipments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.loadDraftShipments),
            switchMap(action => {
                const { includeStates, query } = action;
                const calls = [this.service.getShipments({ ...query, sys_state: 'draft' })];

                if (includeStates && includeStates.length > 0) {
                    includeStates.forEach(state => {
                        const sys_state = state.replace(/_/g, '-').toLowerCase();
                        calls.push(this.service.getShipments({ ...query, sys_state }));
                    });
                }

                return forkJoin(calls).pipe(
                    map(responses => {
                        const allShipments = responses.reduce((acc, res) => acc.concat(res.data), []);
                        const uniqueShipments = allShipments.filter((shipment, index, self) =>
                            index === self.findIndex((s) => s.id === shipment.id)
                        );

                        const firstResponse = responses[0];
                        return ShipmentsActions.loadDraftShipmentsSuccess({
                            shipments: uniqueShipments,
                            customFields: firstResponse?.extra?.custom_field_definitions ?? [],
                            customViews: firstResponse?.extra?.custom_table_views ?? [],
                        });
                    }),
                    catchError(error => {
                        console.error(error);
                        return of(ShipmentsActions.loadDraftShipmentsFailure({ error }));
                    })
                );
            })
        )
    );

    loadShipment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.loadShipment),
            switchMap(action =>
                this.service.getShipment(action.shipmentId).pipe(
                    take(1),
                    map(shipment =>
                        ShipmentsActions.loadShipmentSuccess({
                            shipment,
                        })
                    ),
                    catchError(error => {
                        console.error(error);
                        return of(
                            ShipmentsActions.loadShipmentFailure({ error })
                        );
                    })
                )
            )
        )
    );

    loadSysStates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.loadSysStates),
            switchMap(() =>
                this.service.loadSysStates().pipe(
                    take(1),
                    map(sys_states =>
                        ShipmentsActions.loadSysStatesSuccess({
                            sys_states,
                        })
                    ),
                    catchError(error => {
                        console.error(error);
                        return of(
                            ShipmentsActions.loadSysStatesFailure({ error })
                        );
                    })
                )
            )
        )
    );

    updateShipmentReferences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.updateShipmentReferences),
            switchMap(action =>
                this.service
                    .updateShipmentReferences(action.shipmentId, action.update)
                    .pipe(
                        map(() =>
                            ShipmentsActions.updateShipmentPartiallySuccess(
                                action
                            )
                        ),
                        catchError(error => {
                            console.error(error);
                            return of(
                                ShipmentsActions.updateShipmentPartiallyFailure(
                                    { error }
                                )
                            );
                        })
                    )
            )
        )
    );

    updateShipmentName$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.updateShipmentName),
            switchMap(action =>
                this.service
                    .updateShipmentName(action.shipmentId, action.update)
                    .pipe(
                        map(() =>
                            ShipmentsActions.updateShipmentPartiallySuccess(
                                action
                            )
                        ),
                        catchError(error => {
                            console.error(error);
                            return of(
                                ShipmentsActions.updateShipmentPartiallyFailure(
                                    { error }
                                )
                            );
                        })
                    )
            )
        )
    );

    generateShipmentPdf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.generateShipmentPdf),
            tap(() => {
                this.toastr.info('Your PDF is being generated...');
            }),
            switchMap(action =>
                this.service.generateShipmentPdf(action.shipmentId).pipe(
                    map(blob =>
                        ShipmentsActions.generateShipmentPdfSuccess({
                            blob,
                        })
                    )
                )
            ),
            catchError(error => {
                console.error(error);
                return of(
                    ShipmentsActions.generateShipmentPdfFailure({
                        error,
                    })
                );
            })
        )
    );

    generateShipmentPdfSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShipmentsActions.generateShipmentPdfSuccess),
                map(action => {
                    saveAs(action.blob, 'shipment.pdf');
                })
            ),
        { dispatch: false }
    );

    sendTrackingRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.sendTrackingRequest),
            switchMap(action =>
                this.service
                    .sendTrackingRequest(
                        action.shipmentId,
                        action.trackingRequest
                    )
                    .pipe(
                        map(response =>
                            ShipmentsActions.sendTrackingRequestSuccess({
                                shipmentId: action.shipmentId,
                                response,
                            })
                        ),
                        catchError(error =>
                            of(
                                ShipmentsActions.sendTrackingRequestFailure({
                                    error,
                                })
                            )
                        )
                    )
            )
        )
    );

    cancelTrackingRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.cancelTrackingRequest),
            switchMap(action =>
                this.service.cancelTrackingRequest(action.shipmentId).pipe(
                    map(response =>
                        ShipmentsActions.cancelTrackingRequestSuccess({
                            shipmentId: action.shipmentId,
                            response,
                        })
                    ),
                    catchError(error =>
                        of(
                            ShipmentsActions.cancelTrackingRequestFailure({
                                error,
                            })
                        )
                    )
                )
            )
        )
    );

    cancelTrackingRequestSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ShipmentsActions.cancelTrackingRequestSuccess),
                map(() => {
                    this.toastr.success(
                        'Tracking Request cancelled successfully.'
                    );
                })
            ),
        { dispatch: false }
    );

    updateShipmentDataTransport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.updateShipmentDataTransport),
            switchMap(action =>
                this.service
                    .updateShipmentDataTransport(
                        action.shipmentId,
                        action.changes
                    )
                    .pipe(
                        map(data =>
                            ShipmentsActions.updateShipmentDataTransportSuccess(
                                {
                                    shipmentId: action.shipmentId,
                                    data,
                                }
                            )
                        ),
                        catchError(error =>
                            of(
                                ShipmentsActions.updateShipmentDataTransportFailure(
                                    { error }
                                )
                            )
                        )
                    )
            )
        )
    );

    updateShipmentDataReferences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.updateShipmentDataReferences),
            switchMap(action =>
                this.service
                    .updateShipmentDataReferences(
                        action.shipmentId,
                        action.changes
                    )
                    .pipe(
                        map(data =>
                            ShipmentsActions.updateShipmentDataReferencesSuccess(
                                {
                                    shipmentId: action.shipmentId,
                                    data,
                                }
                            )
                        ),
                        catchError(error =>
                            of(
                                ShipmentsActions.updateShipmentDataReferencesFailure(
                                    { error }
                                )
                            )
                        )
                    )
            )
        )
    );

    assignShipmentOperator$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.assignShipmentOperator),
            switchMap(action =>
                this.service
                    .assignShipmentOperator(
                        action.shipmentId,
                        action.operatorId
                    )
                    .pipe(
                        map(shipment =>
                            ShipmentsActions.assignShipmentOperatorSuccess({
                                shipment,
                            })
                        ),
                        catchError(error =>
                            of(
                                ShipmentsActions.assignShipmentOperatorFailure({
                                    error,
                                })
                            )
                        )
                    )
            )
        )
    );

    completeShipment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.completeShipment),
            switchMap(action =>
                this.service.completeShipment(action.shipmentId).pipe(
                    map(shipment =>
                        ShipmentsActions.completeShipmentSuccess({
                            shipment,
                        })
                    ),
                    catchError(error =>
                        of(ShipmentsActions.completeShipmentFailure({ error }))
                    )
                )
            )
        )
    );

    cancelShipment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.cancelShipment),
            switchMap(action =>
                this.service.cancelShipment(action.shipmentId).pipe(
                    map(shipment =>
                        ShipmentsActions.cancelShipmentSuccess({
                            shipment,
                        })
                    ),
                    catchError(error =>
                        of(ShipmentsActions.cancelShipmentFailure({ error }))
                    )
                )
            )
        )
    );

    genericFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ShipmentsActions.loadShipmentsFailure,
                    ShipmentsActions.loadShipmentFailure,
                    ShipmentsActions.generateShipmentPdfFailure,
                    ShipmentsActions.updateShipmentPartiallyFailure,
                    ShipmentsActions.sendTrackingRequestFailure,
                    ShipmentsActions.cancelTrackingRequestFailure,
                    ShipmentsActions.updateShipmentDataTransportFailure,
                    ShipmentsActions.updateShipmentDataReferencesFailure,
                    ShipmentsActions.assignShipmentOperatorFailure,
                    ShipmentsActions.completeShipmentFailure,
                    ShipmentsActions.cancelShipmentFailure
                ),
                map(action => {
                    this.toastr.error(
                        action.error?.message ??
                            "We're sorry, please try again...",
                        'Something went wrong'
                    );
                })
            ),
        { dispatch: false }
    );

    unselectShipment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.unselectShipment),
            switchMap(() => [
                ResetShipmentPackages(),
                ResetShipmentAddresses(),
                ResetShipmentAttachments(),
                ResetShipmentContainers(),
                ResetShipmentTrackingEvents(),
                ResetShipmentTimelineEvents(),
                ResetShipmentActivities(),
                ResetShipmentRelations(),
                ResetProofOfDeliveries(),
                ResetCustomFields(),
            ])
        )
    );

    loadShipmentAfterSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.selectShipment),
            map((response) => {
                this.store.dispatch(ShipmentsActions.loadShipment({
                    shipmentId: response.selectedId
                }))
            })
        ),
        { dispatch: false }
    );

    calculateFilterSuggestions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.calculateFilterSuggestions),
            switchMap((action) => {
                const { data, columns } = action;
                return this.workerManager.calculatePopularValues(data, columns).pipe(
                    map(suggestions =>
                        ShipmentsActions.calculateFilterSuggestionsSuccess({ suggestions })
                    ),
                    catchError(error => {
                        console.error('[ShipmentsEffects] Failed to calculate filter suggestions:', error);
                        return of(
                            ShipmentsActions.calculateFilterSuggestionsFailure({ error })
                        );
                    })
                );
            })
        )
    );

    calculateSuggestionsOnLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ShipmentsActions.loadShipmentsSuccess),
            map(({ shipments }) => {
                // Only calculate suggestions if we have data
                if (shipments && shipments.length > 0) {
                    // Define columns to analyze
                    const columns = [
                        'data.shipper_name',
                        'data.consignee_name',
                        'data.vessel',
                        'data.port_of_loading_locode',
                        'data.port_of_discharge_locode',
                        'data.place_of_receipt_locode',
                        'data.place_of_delivery_locode'
                    ];
                    return ShipmentsActions.calculateFilterSuggestions({
                        data: shipments,
                        columns
                    });
                }
                // If no shipments, skip calculation
                return ShipmentsActions.calculateFilterSuggestionsSuccess({ suggestions: {} });
            })
        )
    );

    constructor(
        private actions$: Actions,
        private toastr: ToastrService,
        private service: ShipmentsServiceBase,
        private store: Store<fromShipments.ShipmentsPartialState>,
        private workerManager: WorkerManagerService
    ) {}
}
